<script setup lang="ts">
import type { CmsElementMsbBlog } from "~/composables/useCustom";
import { useProgressBar } from '@/composables/useProgressBar';
import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { formatDate, formatTime } from '~/composables/useUtils';

const containerRef = ref<HTMLElement | null>(null);
useProgressBar(containerRef); // Attach progress bar logic

const props = defineProps<{
  content: CmsElementMsbBlog;
}>();

const { result } = props?.content?.data;
const { locale, t } = useI18n();
const route = useRoute();
interface BlogTag {
  name: string;
}

interface Blog {
  tags?: BlogTag[];
  translated?: {
    title?: string;
    slug?: string;
    teaser?: string;
    contents?: string;
  };
  image?: {
    url?: string;
    alt?: string;
    mimeType?: string;
  };
  author?: {
    name?: string;
    link?: string;
    image?: {
      url?: string;
      alt?: string;
      mimeType?: string;
    };
    customFields?: Record<string, any>;
  };
  createdAt?: string;
  postdate?: string;
}

const router = useRouter();

// Make filter value reactive
const filterValue = ref<string | undefined>();

// Watch for route query changes and update filter value
watch(
  () => route.query.filter,
  (newFilter) => {
    filterValue.value = Array.isArray(newFilter) ? newFilter[0] : newFilter;
  },
  { immediate: true }
);

// Watch for filter value changes and update filtered blogs
const filteredBlogs = computed(() => {
  const blogs = (result?.elements || []) as Blog[];
  
  if (filterValue.value && blogs.length > 0) {
    return blogs.filter((blog) => {
      return blog?.tags?.some((tag) => 
        tag?.name?.toLowerCase() === filterValue.value?.toLowerCase()
      );
    });
  }
  
  return blogs;
});

const { title, subtitle, linkText, allItemLink } = props.content.translated.config;

const removeTagFilter = () => {
  const query = { ...route.query };
  delete query.filter;
  router.push({ query });
};

// Optional: Add method to programmatically set filter
const setFilter = (tag: string) => {
  const query = { ...route.query, filter: tag };
  router.push({ query });
};

// Optional: Add method to check if a tag is currently filtered
const isTagActive = (tagName: string) => {
  return filterValue.value?.toLowerCase() === tagName.toLowerCase();
};

function getBlogLinkAriaLabel(blog: Blog): string {
  const title = blog?.translated?.title;
  return title ? t("blog.readBlogPost", { title }) : t("blog.readMore");
}
</script>

<template>
  <div 
    ref="containerRef" class="blog !m-0"
    :class="{
      'py-16': route.path === '/',
      'pt-0 pb-12': route.path !== '/'
    }"
  >
    <div class="blog-inner relative">

      <div class="title" v-if="subtitle?.value || title?.value">
        <span class="sub-heading text-base 3xl:text-md font-marcellus text-purple" v-if="subtitle?.value">{{ subtitle.value }}</span>
        <h2 id="blog-title" class="heading text-2xl lg:text-6xl 3xl:text-10xl font-marcellus text-grey-700 leading-[30px] md:leading-none" v-if="title?.value">
          {{ title.value }}
        </h2>
      </div>


       <ul
          v-if="filterValue"
          class="flex flex-wrap mb-4 mt-8 list-none">
            <li
              class="bg-light-grey rounded-lg py-2.5 px-3.5 inline-flex items-center mb-2 mr-2 min-h-[36px]"
            >
              <span class="font-mulish font-normal text-sm 3xl:text-base text-grey-700">
                {{ filterValue }}
              </span>
              <button 
                class="text-purple hover:text-red ml-2.5" 
                :aria-label="$t('productListing.ariaLabelRemoveFilter')"
                @click="removeTagFilter"
              >
                <i class="icon-close-outline font-bold text-xs"></i>
              </button>
            </li>
        </ul>


      <div
        class="content mt-7 lg:mt-10"
        :class="{
          'flex flex-nowrap items-start md:items-start md:flex-wrap hide-scrollbar -mx-5 px-5 md:px-0 md:mx-0 md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 gap-y-12 overflow-x-scroll md:overflow-x-hidden snap-x snap-mandatory': route.path === '/',
          'flex flex-wrap md:items-start md:flex-wrap md:grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 gap-y-12': route.path !== '/'
        }"
      >
        <!-- Blog Card -->
        <template v-if="filteredBlogs && filteredBlogs.length">
          <div 
            v-for="(blog, index) in filteredBlogs"
            :key="index"
            class="blog-card bg-white overflow-hidden group min-w-[100%] md:min-w-[0px] snap-center border-b border-gray-200 pb-2 md:mb-5">
            <div class="relative pb-[50%] rounded-[15px] bg-grey-100 md:rounded-[20px] overflow-hidden">
              <NuxtLink 
                v-if="blog?.translated?.slug"
                :to="`/blog/${blog.translated.slug}`"
                :aria-label="getBlogLinkAriaLabel(blog)"
              >
                <SharedImage
                  loading="lazy"
                  v-if="blog?.image?.url"
                  :src="blog.image.url"
                  :alt="blog.image.alt ? blog.image.alt : $t('mysofbed')"
                  imgClass="absolute inset-0 w-full h-full object-contain group-hover:scale-105 transition-all duration-300 bg-primary/5"
                  :sizes="{ desktop: '524x295', tablet: '291x163', mobile: '612x344' }"
                  format="webp"
                  :mimeType="blog.image?.mimeType"
                />
              </NuxtLink>
            </div>
            <div>
              <ClientOnly>
                <div class="text-md mt-5" v-if="blog?.postdate">
                  <span class="text-primary">{{ formatDate(blog.postdate, locale) }}</span>
                  <span class="text-grey-700">· {{ formatTime(blog.postdate, locale, false) }}</span>
                </div>
              </ClientOnly>
              <NuxtLink 
                v-if="blog?.translated && blog.translated.title && blog.translated.slug"
                :to="`/blog/${blog.translated.slug}`" 
                class="block mt-4" 
                :aria-label="getBlogLinkAriaLabel(blog)"
              >
                <h3
                  class="text-xl 3xl:text-2xl leading-[1.2] font-marcellus text-grey-700 line-clamp-2 hover:text-primary transition-all duration-300 min-h-[55px] 3xl:min-h-[60px]">
                  {{ blog.translated?.title }}
                </h3>
              </NuxtLink>
              <p class="text-gray-600 mt-2 line-clamp-3 min-h-[72px]">
                {{ blog.translated?.teaser || (blog.translated?.contents ? blog.translated.contents.replace(/<[^>]*>/g, '').trim() : '') || '' }}
              </p>
              <!-- New Author Section -->
              <div class="author-section flex items-center justify-between my-5 p-2 bg-[#F8F9FA] rounded-xl gap-x-4">
                <div v-if="blog.author" class="author-info flex items-start gap-3">
                  <div class="author-avatar w-8 h-8 rounded-full overflow-hidden flex-shrink-0 bg-grey-100 mt-1">
                    
                    <SharedImage  
                      loading="lazy"
                      v-if="blog.author?.image?.url"
                      :src="blog.author?.image?.url"
                      :alt="blog.author?.image?.alt ? blog.author?.image?.alt : $t('mysofbed')"
                      fetchpriority="high"
                      class="w-full h-full object-cover"
                      :sizes="{ desktop: '65x65',  tablet: '65x65', mobile: '65x65'}"
                      format="webp"
                      :mimeType="blog.author?.image?.mimeType"
                    />
                    <div v-else class="w-full h-full flex items-center justify-center text-sm font-bold text-grey-500 uppercase">
                      {{ blog.author?.name?.charAt(0) || '' }}
                    </div>
                  </div>
                  <div>
                    <p class="author-name text-base font-bold text-grey-700 leading-tight">{{ blog.author?.name || '' }}</p>
                    <p class="author-name text-sm text-grey-500">{{ blog.author?.customFields?.custom_blog_author_fields_designation || '' }}</p>
                  </div>
                </div>
                <div v-else class="flex items-center gap-3">
                  <div class="author-avatar w-8 h-8 overflow-hidden flex-shrink-0 bg-grey-100">
                    <img src="/assets/favicon/windows-touch-icon-mysofabed.png" alt="My Sofa Bed" class="w-full h-full object-cover" />
                  </div>
                  <p class="author-name text-base font-bold text-grey-700 leading-tight capitalize">
                    {{ $t('mySofaBed') }}
                  </p>
                </div>
                <NuxtLink
                  v-if="blog?.translated?.slug"
                  :to="`/blog/${blog.translated.slug}`"
                  class="text-grey-700 hover:text-purple hover:underline transition-all duration-300 font-medium inline-block underline whitespace-nowrap"
                  :aria-label="getBlogLinkAriaLabel(blog)"
                >
                  {{ $t('blog.readMore') }}
                </NuxtLink>
              </div>
            </div>
          </div>
        </template>
      </div>

      <div
        v-if="route.path === '/'"
        class="progress-bar block md:hidden mt-8 h-[6px] bg-gray-300 rounded-full mx-10">
        <div class="progress-bar-inner h-[6px] bg-primary transition-all ease-linear rounded-full w-[0%]"></div>
      </div>
      
      <div v-if="linkText?.value" class="btn-wrapper mt-10 md:mt-0 md:absolute md:top-0 md:right-0 text-center">
        <NuxtLink 
          v-if="allItemLink?.value"
          :to="allItemLink.value"
          class="btn btn-primary btn-icon-end"
        >
          <span class="my-auto" v-if="linkText?.value">{{ linkText.value }}</span>
          <i class="icon-arrow-right" aria-hidden="true"></i>
        </NuxtLink>
      </div>
    </div>
  </div>
</template>